<template>
  <div class="action_end_box">
    <!-- 1. 映射关系看板 (Source -> Target) -->
    <div class="end_mapping_board">
      <div class="board_inner">
        <!-- 当前按键 -->
        <div class="key_slot source">
          <div class="slot_label">{{ $t('current_selected_key') }}</div>
          <div class="key_display active">{{ currentKeyDisp }}</div>
        </div>

        <!-- 连动装饰 -->
        <div class="mapping_flow">
          <div class="flow_icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <path d="M12 7v5l3 2" stroke-width="2" />
            </svg>
          </div>
          <div class="flow_label">END</div>
        </div>

        <!-- 目标按键 -->
        <trigger_btn class="key_slot target" :label="$t('mapping_action')" :vks="Keys[0]?.KeyCode || []" :tab="tab" :show-edit="true" :large="true" @trigger-click="showKeyDialog = true" />
      </div>
    </div>

    <!-- 2. 设置区块 -->
    <div class="end_settings_section" v-if="showDelayTime">
      <div class="mgx_setting_item_card">
        <div class="setting_header">
          <div class="header_left">
            <svg class="pill_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <path d="M12 6v6l4 2" stroke-width="2" stroke-linecap="round" />
            </svg>
            <span class="pill_title">{{ $t('action_set_delay_time') }}</span>
          </div>
          <span class="pill_value">{{ delayTime }}<span>ms</span></span>
        </div>
        <div class="setting_slider_container">
          <input v-model.number="delayTime" class="slider_input" type="range" max="1000" min="0" step="1" :style="{ backgroundSize: timeBackgroundSize }" />
          <div class="slider_labels">
            <span>0ms</span>
            <span>1000ms</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 按键选择弹窗 -->
    <action_key_dialog v-model:visible="showKeyDialog" :row-index="0" :base-tab="tab" :selected-keys="Keys[0]?.KeyCode || [0, 0, 0]" @confirm="onKeyConfirm" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import trigger_btn from './trigger_btn.vue'
import action_key_dialog from './action_key_dialog.vue'
import { actionSet } from '@/service/driver/action_set'
import { SetType, BaseTabs } from '@/service/driver/action_types'
import { MagnetSeniorType } from '@libs/types/enum'
import { VK, kks } from '@/define/define'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { normalizeKeyCode } from '@/tool/utility'
import { operate } from '@/service/driver/operate'
import { currentSelectedKeyText, getVkText } from './action_config'

const props = defineProps<{ tab: BaseTabs; keys: VK[] }>()
const showKeyDialog = ref(false)
const delayTime = ref(0)
let { Keys, KeyType, DelayTime } = toRefs(actionSet.magnetValue)

const currentKeyDisp = computed(() => currentSelectedKeyText.value)

const targetKeyName = computed(() => {
  const code = Keys.value[0]?.KeyCode?.[2] || 0
  return getVkText(code)
})

const hasTargetKey = computed(() => Keys.value[0]?.KeyCode.some((k) => k > 0))

const timeBackgroundSize = computed(() => {
  const timePercent = ((delayTime.value - 0) / (1000 - 0)) * 100
  return `${timePercent}%, 100%`
})

const showDelayTime = ref(false)

const initData = () => {
  if (Keys.value.length != 1) {
    Keys.value = [
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [],
        Travel: 0,
      },
    ]
    DelayTime.value = 0
  }
  if (KeyType.value == MagnetSeniorType.SeniorKeyTypeEND) {
    actionSet.initBaseKeysBySwitchInput(Keys.value[0].KeyCode)
  } else {
    KeyType.value = MagnetSeniorType.SeniorKeyTypeEND
  }
  delayTime.value = actionSet.modeTimes[SetType.end]
}

onMounted(() => {
  initData()
  showDelayTime.value = specialCheckCurrent(speciaBehiver.SupportEndDelayTime)
})

watch(
  () => JSON.stringify(actionSet.magnetValue),
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      initData()
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => operate.currentKey.value,
  () => {
    initData()
  },
)

watch(delayTime, (val) => {
  const v = Number(val)
  actionSet.modeTimes[SetType.end] = v
  DelayTime.value = v
})

onUnmounted(() => {
  showDelayTime.value = false
})

const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  if (Keys.value[0]) {
    Keys.value[0].KeyCode = keys
  }
}
</script>

<style lang="less" scoped>
.action_end_box {
  margin: auto;
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;

  .end_mapping_board {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px);
    border-radius: var(--radius-l);
    padding: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.8);

    .board_inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 30px;
    }

    .key_slot {
      width: 130px;
      height: 80px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 12px 14px;
      border-radius: var(--radius-l);
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(0, 0, 0, 0.03);
      transition: var(--transition-base);

      .slot_label {
        font-size: 12px;
        color: var(--color-text-hint);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 2px;
      }

      .key_display {
        font-size: 22px;
        font-weight: 900;
        color: var(--color-dark);
        line-height: 1.2;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          color: var(--brand-color-pure);
        }
      }

      &.target {
        cursor: pointer;
        position: relative;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        padding: 0;
        height: 80px;

        &:hover {
          border-color: var(--brand-color-pure);
          box-shadow: var(--action-btn-shadow-hover);
          transform: translateY(-3px);
        }

        &.empty {
          .key_display {
            font-size: 14px;
            color: var(--color-text-hint);
            font-weight: 600;
          }
        }

        .edit_hint {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 14px;
          height: 14px;
          color: var(--color-text-hint);
          opacity: 0.4;
        }
      }
    }

    .mapping_flow {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;

      .flow_icon {
        width: 24px;
        height: 24px;
        color: var(--brand-color-pure);
        filter: drop-shadow(0 0 8px rgba(255, 124, 0, 0.3));
      }

      .flow_label {
        font-size: 9px;
        font-weight: 900;
        color: var(--brand-color-pure);
        opacity: 0.7;
      }
    }
  }

  .end_settings_section {
    width: 100%;
  }
}
</style>
